import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { StatusFilter, FilterBanner } from './platformFilters'
import PlatformConfirm from './platformConfirm'
import CompanyFormModal from './companyFormModal'
import { SkeletonRows } from '../../components/skeleton'
import { getCompanies, suspendCompany, reactivateCompany } from '../../services/platform'
import { onQuerySuccess, onMutateSuccess } from '../../utils/queryResponse'
import refreshPlatform from './refreshPlatform'
import { dateLocale } from '../../i18n/i18n'

const AVATAR_COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4', '#F97316']
const getInitials = (name) => { if (!name) return '?'; const parts = name.trim().split(/\s+/); return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase() }
const getColor = (name) => { let h = 0; for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h); return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length] }

// ?status= values the list understands — set by the dashboard's Active /
// Suspended cards or the Filters menu. Anything else means "all".
const STATUSES = ['active', 'suspended']
const BANNER_I18N = {
    active: { label: 'platform.companies.bannerActive', hint: 'platform.companies.bannerActiveHint' },
    suspended: { label: 'platform.companies.bannerSuspended', hint: 'platform.companies.bannerSuspendedHint' },
}

const PlatformCompaniesPage = () => {
    const { t } = useTranslation()
    const qc = useQueryClient()
    const [searchParams, setSearchParams] = useSearchParams()
    const status = STATUSES.includes(searchParams.get('status')) ? searchParams.get('status') : ''
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [companies, setCompanies] = useState([])
    const [total, setTotal] = useState(0)
    // null = closed, { company: null } = create, { company } = edit
    const [companyForm, setCompanyForm] = useState(null)
    const [actionOpen, setActionOpen] = useState(null)
    const [confirm, setConfirm] = useState(null)
    const limit = 20

    const { isFetching, data } = useQuery(['platform-companies', page, search, status], () => getCompanies({ page, limit, search, status }), { keepPreviousData: true })

    const setStatus = (next) => {
        setPage(1)
        setSearchParams(next ? { status: next } : {})
    }

    useEffect(() => { onQuerySuccess(data, (r) => { setCompanies(r.rows || []); setTotal(r.total || 0) }) }, [data])

    const suspendMut = useMutation(suspendCompany, { onSuccess: onMutateSuccess(() => refreshPlatform(qc), t('platform.companyActions.suspendedToast')) })
    const reactivateMut = useMutation(reactivateCompany, { onSuccess: onMutateSuccess(() => refreshPlatform(qc), t('platform.companyActions.reactivatedToast')) })

    const totalPages = Math.ceil(total / limit) || 1

    return (
        <div className="sbr">
            <div className="pl-page-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#0B1424' }}>{t('platform.companies.title')}</h2>
                    <p style={{ fontSize: 13, color: '#697586', margin: 0 }}>{t('platform.companies.subtitle')}</p>
                </div>
                <button className="btn-sbr btn-primary-sbr" onClick={() => setCompanyForm({ company: null })} style={{ padding: '8px 18px', fontSize: 13, fontWeight: 600, borderRadius: 8 }}>{t('platform.companies.add')}</button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                <StatusFilter value={status} onChange={setStatus} options={[
                    { value: 'active', label: t('platform.common.active') },
                    { value: 'suspended', label: t('platform.common.suspended') },
                ]} />
                <div className="p-search-bar" style={{ flex: 1 }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#9CA3B0" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                    <input placeholder={t('platform.companies.search')} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} />
                </div>
            </div>

            {status && (
                <FilterBanner
                    highlight={t(BANNER_I18N[status].label, { count: total })}
                    hint={t(BANNER_I18N[status].hint)}
                    onClear={() => setStatus('')}
                />
            )}

            <div className="card-sbr" style={{ padding: 0 }}>
                {isFetching && companies.length === 0 ? (
                    <div style={{ padding: 20 }}><SkeletonRows rows={6} widths={['25%', '20%', '15%', '15%', '10%']} /></div>
                ) : (
                    <>
                        <table className="p-table">
                            <thead>
                                <tr>
                                    <th>{t('platform.columns.company')}</th>
                                    <th className="p-col-opt">{t('platform.columns.plan')}</th>
                                    <th className="p-col-opt">{t('platform.columns.users')}</th>
                                    <th>{t('platform.columns.status')}</th>
                                    <th className="p-col-opt">{t('platform.columns.created')}</th>
                                    <th style={{ width: 50 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {companies.map(c => (
                                    <tr key={c.id}>
                                        <td>
                                            <Link to={`/platform/companies/${c.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: 'inherit' }}>
                                                <div style={{ width: 34, height: 34, borderRadius: '50%', background: getColor(c.name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{getInitials(c.name)}</div>
                                                <div>
                                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0B1424' }}>{c.name}</div>
                                                    {c.email && <div style={{ fontSize: 11, color: '#9CA3B0' }}>{c.email}</div>}
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="p-col-opt" style={{ fontSize: 12, color: '#697586' }}>{c.plan?.name || '—'}</td>
                                        <td className="p-col-opt" style={{ fontSize: 12, color: '#697586' }}>{c.userCount ?? 0}</td>
                                        <td>
                                            <span className={`p-status-badge ${c.isSuspended ? 'p-status-suspended' : 'p-status-active'}`}>
                                                {c.isSuspended ? t('platform.common.suspended') : t('platform.common.active')}
                                            </span>
                                        </td>
                                        <td className="p-col-opt" style={{ fontSize: 12, color: '#697586' }}>{c.createdAt ? new Date(c.createdAt).toLocaleDateString(dateLocale(), { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                                        <td>
                                            <div style={{ position: 'relative' }}>
                                                <button style={{ background: 'none', border: '1px solid #EBEEF5', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#697586' }} onClick={() => setActionOpen(actionOpen === c.id ? null : c.id)}>
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" /></svg>
                                                </button>
                                                {actionOpen === c.id && (
                                                    <div className="p-action-dropdown">
                                                        <Link to={`/platform/companies/${c.id}`} className="p-action-dropdown-item" style={{ textDecoration: 'none', color: '#1C2536' }} onClick={() => setActionOpen(null)}>
                                                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                                            {t('platform.companyActions.viewDetails')}
                                                        </Link>
                                                        <button className="p-action-dropdown-item" onClick={() => { setCompanyForm({ company: c }); setActionOpen(null) }}>
                                                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                                            {t('platform.companyActions.edit')}
                                                        </button>
                                                        <div className="p-action-dropdown-divider" />
                                                        {c.isSuspended ? (
                                                            <button className="p-action-dropdown-item" onClick={() => { reactivateMut.mutate(c.id); setActionOpen(null) }}>
                                                                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#16A34A" strokeWidth="2"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>
                                                                {t('platform.companyActions.reactivate')}
                                                            </button>
                                                        ) : (
                                                            <button className="p-action-dropdown-item p-action-dropdown-danger" onClick={() => { setConfirm({ action: 'suspend', id: c.id, name: c.name }); setActionOpen(null) }}>
                                                                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
                                                                {t('platform.companyActions.suspend')}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {companies.length === 0 && !isFetching && (
                                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9CA3B0', padding: 32 }}>{t('platform.companies.empty')}</td></tr>
                                )}
                            </tbody>
                        </table>

                        {totalPages > 1 && (
                            <div className="p-pagination">
                                <div className="p-pagination-info">{t('platform.common.showing', { from: (page - 1) * limit + 1, to: Math.min(page * limit, total), total })}</div>
                                <div className="p-pagination-controls">
                                    <button className="p-pagination-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, page - 3), page + 2).map(p => (
                                        <button key={p} className={`p-pagination-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                                    ))}
                                    <button className="p-pagination-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <PlatformConfirm confirm={confirm} onClose={() => setConfirm(null)} onConfirm={(c) => suspendMut.mutate(c.id)} />

            {companyForm && <CompanyFormModal company={companyForm.company} onClose={() => setCompanyForm(null)} />}
        </div>
    )
}

export default PlatformCompaniesPage
